/**
 * @module
 * @format
 */

import {from, push} from '@acng/frontend-bounty/std/array.js';
import {createSet, add, has, remove} from '@acng/frontend-bounty/collection.js';
import {createGlobalContext, GlobalWatch} from '@acng/frontend-relativity';
import {CTX_PROVIDE} from '@acng/frontend-relativity/minify';
import {guard, asAmateurIdList, $creator, asCreatorUpdateEvent} from '../../model';
/**
 * @import {AmateurId} from '../../model';
 */
import {inject} from 'acng/core/service/ng';
import {updateAmateur} from './cache.js';
import {ctxEventBus, listen} from '../../core/context/event-bus.js';
import {cdn} from '../../core/service/cdn.js';

const VERBOSE = false;

/**
 * @param {AmateurId} amateurId
 */
export const isOnline = (amateurId) => has(registry, amateurId);

/**
 * Observe the (messenger-) online state of Amateurs.
 */
export const ctxOnline = createGlobalContext(isOnline);

/**
 * @type {Set<AmateurId>}
 */
const registry = createSet();

/**
 * @param {AmateurId[]} list
 * @returns {Promise<AmateurId[]>}
 */
export async function onlineFirst(list) {
  await init;

  return list.slice().sort((a, b) => {
    const indexA = has(registry, a) ? 1 : -1;
    const indexB = has(registry, b) ? 1 : -1;
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

/**
 * @deprecated
 */
export const getOnlineArray = async () => {
  await init;
  return from(registry);
};

GlobalWatch(ctxEventBus)(async (eventBusIsOnline) => {
  if (!eventBusIsOnline) {
    DEBUG: if (VERBOSE) console.info('Event bus is offline. Amateur online state is frozen.');

    return;
  }

  DEBUG: if (VERBOSE) console.info('Fetch amateur online list...');
  const data = await cdn(`/api/amateur-profile/online`);

  DEBUG: if (VERBOSE) console.info('...got online list.', data);
  ASSERT: guard(data, asAmateurIdList);

  const updated = createSet(data);

  /** @type {AmateurId[]} */
  const updateOffline = [];

  /** @type {AmateurId[]} */
  const updateOnline = [];

  for (const id of registry) {
    if (!has(updated, id)) {
      push(updateOffline, id);
    }
  }

  for (const id of updated) {
    if (!has(registry, id)) {
      push(updateOnline, id);
    }
  }

  for (const id of updateOffline) {
    remove(registry, id);
    updateAmateur({id, is: {online: false, offline: true}});
  }

  for (const id of updateOnline) {
    add(registry, id);
    updateAmateur({id, is: {online: true, offline: false}});
  }

  DEBUG: VERBOSE && console.log('Amateur online registry is up to date.', registry);

  resolveInit();
  ctxOnline[CTX_PROVIDE](null, isOnline);

  try {
    inject('$rootScope').$digest();
  } catch (reason) {
    //
  }
});

listen($creator, (data) => {
  guard(data, asCreatorUpdateEvent, '<creator-event>');
  const {creator} = data;

  (creator.is?.online ? add : remove)(registry, creator.id);
  updateAmateur(creator);
  ctxOnline[CTX_PROVIDE](null, isOnline);
  try {
    inject('$rootScope').$digest();
  } catch (e) {}
});

/** @type {() => void} */
let resolveInit;

/** @type {Promise<void>} */
let init = new Promise((resolve) => (resolveInit = resolve));
